import type { FieldDefinitionType } from '#root/components/dynamicForm/types/field.type.ts';
import type { CheckBoxGridModelType, InputSearchModelType } from '@c4-links/vue3-links-components';
import type { FilterDefinitionValue } from '@carrefour-gcs/bff-sdk';
import { ANONYMIZED_LABEL_MASK } from '@carrefour-gcs/shared';
import { computed, ref, watch, type Ref } from 'vue';
import { numberAnonymizedLabels } from '../utils/anonymizedLabel.utils';

const hasSearchValue = (searchValue: InputSearchModelType | undefined): boolean => {
  const value = searchValue?.searchValue;

  return Array.isArray(value) ? value.length > 0 : Boolean(value);
};

/**
 * composable for orchestrating checkbox grid filter logic
 *
 * The selection is kept apart from the displayed options, so a value stays selected when it is not displayed
 * (search results, or values outside of the first page of `field.options`).
 * Without search, the selected values missing from `field.options` are displayed before them.
 */
export const useCheckboxGridSelection = ({
  field,
  searchData,
  searchValue,
  initialValue,
  onSelectionChange,
  fetchValuesByKeys,
  isAnonymized = ref(false)
}: {
  initialValue: Ref<unknown>;
  field: FieldDefinitionType;
  searchValue: Ref<InputSearchModelType | undefined>;
  searchData: Readonly<Ref<FilterDefinitionValue[] | undefined>>;
  onSelectionChange: (selections: string[]) => void;
  fetchValuesByKeys?: (keys: string[]) => Promise<FilterDefinitionValue[]>;
  isAnonymized?: Readonly<Ref<boolean>>;
}) => {
  const modelValue = ref<CheckBoxGridModelType[]>([]);
  const selectedOptions = ref(new Map<string, FilterDefinitionValue>());
  // Options behind the displayed checkboxes, before their masked labels are numbered
  let displayedOptions = new Map<string, FilterDefinitionValue>();

  // Computed from the displayed checkboxes too, so the count is up to date before the selection is synced
  const nbSelectedCheckboxGridItems = computed(() => {
    const displayedValues = new Set(modelValue.value.map((item) => item.value));
    const nbHiddenSelected = [...selectedOptions.value.keys()].filter((key) => !displayedValues.has(key)).length;

    return nbHiddenSelected + modelValue.value.filter((item) => item.checked).length;
  });

  // ———— B ———— Functions ————————————————————————————————————————————————————————————————————————————————

  /**
   * Options displayed without search: selected values missing from `field.options`, then `field.options`.
   */
  const getDefaultOptions = (): FilterDefinitionValue[] => {
    const fieldOptions = field.options ?? [];
    const fieldOptionValues = new Set(fieldOptions.map((option) => option.value));
    const selectedOutsideFieldOptions = [...selectedOptions.value.values()].filter(
      (option) => !fieldOptionValues.has(option.value)
    );

    return [...selectedOutsideFieldOptions, ...fieldOptions];
  };

  const setDisplayedOptions = (options: FilterDefinitionValue[]) => {
    displayedOptions = new Map(options.map((option) => [option.value, option]));
    modelValue.value = numberAnonymizedLabels(options).map((option) => ({
      disabled: false,
      label: option.label,
      value: option.value,
      description: option.description,
      checked: selectedOptions.value.has(option.value)
    })) as CheckBoxGridModelType[];
  };

  /**
   * Option stored in the selection from a displayed checkbox: the source option, so the code line is
   * kept and a masked label is numbered again wherever it is displayed next.
   */
  const toSelectedOption = (item: CheckBoxGridModelType): FilterDefinitionValue =>
    displayedOptions.get(item.value) ?? ({ value: item.value, label: item.label } as FilterDefinitionValue);

  const emitSelection = () => onSelectionChange([...selectedOptions.value.keys()]);

  /**
   * Fetches the labels of the selected values missing from `field.options`, to display them.
   */
  const fetchMissingSelectedOptions = async (missingKeys: string[]) => {
    if (!fetchValuesByKeys || missingKeys.length === 0) {
      return;
    }

    try {
      const fetchedOptions = await fetchValuesByKeys(missingKeys);

      fetchedOptions
        .filter((option) => selectedOptions.value.has(option.value))
        .forEach((option) => selectedOptions.value.set(option.value, option));
    } catch (error) {
      console.error('[useCheckboxGridSelection] Error fetching selected values:', error);
    }

    if (!hasSearchValue(searchValue.value)) {
      setDisplayedOptions(getDefaultOptions());
    }
  };

  const initialize = async () => {
    const initialIds = Array.isArray(initialValue.value) ? (initialValue.value as string[]) : [];
    const fieldOptionsMap = new Map((field.options ?? []).map((option) => [option.value, option]));

    // Labels default to the key until the missing values are fetched, never for an anonymized user
    const unresolvedLabel = (id: string) => (isAnonymized.value ? ANONYMIZED_LABEL_MASK : id);

    selectedOptions.value = new Map(
      initialIds.map((id) => [
        id,
        fieldOptionsMap.get(id) ?? ({ value: id, label: unresolvedLabel(id) } as FilterDefinitionValue)
      ])
    );

    setDisplayedOptions(getDefaultOptions());

    await fetchMissingSelectedOptions(initialIds.filter((id) => !fieldOptionsMap.has(id)));
  };

  const selectAll = () => {
    modelValue.value.forEach((item) => {
      if (!item.disabled) {
        item.checked = true;
        selectedOptions.value.set(item.value, toSelectedOption(item));
      }
    });

    emitSelection();
  };

  const deselectAll = () => {
    selectedOptions.value.clear();
    modelValue.value.forEach((item) => {
      item.checked = false;
    });

    emitSelection();
  };

  // ———— E ———— Functions ————————————————————————————————————————————————————————————————————————————————

  // ———— B ———— Watchers ————————————————————————————————————————————————————————————————————————————————

  /**
   * Displays the search results while searching, and the default options otherwise.
   */
  watch(
    [searchData, searchValue],
    ([newData, newSearchValue]) => {
      setDisplayedOptions(hasSearchValue(newSearchValue) ? (newData ?? []) : getDefaultOptions());
    },
    { deep: true }
  );

  /**
   * Syncs the selection with the displayed checkboxes: hidden selected values are kept.
   */
  watch(
    modelValue,
    (newValue) => {
      let hasChanged = false;

      newValue.forEach((item) => {
        const isSelected = selectedOptions.value.has(item.value);

        if (item.checked && !isSelected) {
          selectedOptions.value.set(item.value, toSelectedOption(item));
          hasChanged = true;
        } else if (!item.checked && isSelected) {
          selectedOptions.value.delete(item.value);
          hasChanged = true;
        }
      });

      if (hasChanged) {
        emitSelection();
      }
    },
    { deep: true }
  );

  // ———— E ———— Watchers ————————————————————————————————————————————————————————————————————————————————

  return {
    modelValue,
    nbSelectedCheckboxGridItems,
    selectAll,
    initialize,
    deselectAll
  };
};
